import { useMemo, useState } from 'react';
import { Dropdown, DropdownContainer } from '@epam/uui';
import { Checkbox, SearchInput, Tag, Text } from '@epam/promo';
import { useUserGroupsGetQuery } from 'hooks/api/userGroups';
import { IUserGroup } from 'models/userGroup.model';
import { getSelectedUserRoleDisplayItems } from './userRolesDisplay';
import css from './UserRolesPicker.module.scss';

interface UserRolesPickerProps {
  value: string[];
  onChange: (roleIds: string[]) => void;
  isDisabled?: boolean;
}

export const UserRolesPicker = ({
  value,
  onChange,
  isDisabled,
}: UserRolesPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { data: groups = [] } = useUserGroupsGetQuery();

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return groups;
    const query = search.toLowerCase();

    return groups
      .map((group) => {
        const isGroupMatched = group.name.toLowerCase().includes(query);

        if (isGroupMatched) {
          return group;
        }

        return {
          ...group,
          roles: group.roles.filter((role) =>
            role.name.toLowerCase().includes(query),
          ),
        };
      })
      .filter((group) => group.roles.length > 0);
  }, [groups, search]);

  const getGroupState = (group: IUserGroup) => {
    const roleIds = group.roles.map((r) => r.id);

    if (roleIds.length === 0) {
      return {
        checked: false,
        indeterminate: false,
      };
    }

    const selectedCount = roleIds.filter((id) => value.includes(id)).length;

    return {
      checked: selectedCount === roleIds.length,
      indeterminate: selectedCount > 0 && selectedCount < roleIds.length,
    };
  };

  const toggleGroup = (group: IUserGroup) => {
    const roleIds = group.roles.map((r) => r.id);
    const allSelected = roleIds.every((id) => value.includes(id));

    if (allSelected) {
      onChange(value.filter((id) => !roleIds.includes(id)));
    } else {
      onChange([...new Set([...value, ...roleIds])]);
    }
  };

  const toggleRole = (roleId: string) => {
    if (value.includes(roleId)) {
      onChange(value.filter((id) => id !== roleId));
    } else {
      onChange([...value, roleId]);
    }
  };

  const selectedRoles = useMemo(
    () =>
      getSelectedUserRoleDisplayItems({
        selectedRoleIds: value,
        userGroups: groups,
      }),
    [groups, value],
  );

  const triggerLabel =
    value.length === 0
      ? 'Select user roles'
      : `${value.length} role${value.length === 1 ? '' : 's'} selected`;

  const handleOpenChange = (open: boolean) => {
    if (isDisabled) return;
    setIsOpen(open);
    if (!open) setSearch('');
  };

  return (
    <div className={css.container}>
      <Dropdown
        closeOnClickOutside
        closeOnEscape
        isNotUnfoldable={isDisabled}
        renderBody={(props) => (
          <DropdownContainer {...props} maxWidth={420}>
            <div className={css.dropdownBody}>
              <div className={css.searchWrapper}>
                <SearchInput
                  cx={css.searchInput}
                  placeholder="Search skills or groups..."
                  size="30"
                  value={search}
                  onValueChange={(v) => setSearch(v ?? '')}
                />
              </div>
              <div className={css.list}>
                {filteredGroups.map((group) => {
                  const { checked, indeterminate } = getGroupState(group);

                  return (
                    <div key={group.id}>
                      <div className={css.groupRow}>
                        <Checkbox
                          indeterminate={indeterminate}
                          label={group.name}
                          value={checked}
                          onValueChange={() => toggleGroup(group)}
                        />
                      </div>
                      {group.roles.map((role) => (
                        <div key={role.id} className={css.roleRow}>
                          <Checkbox
                            label={role.name}
                            value={value.includes(role.id)}
                            onValueChange={() => toggleRole(role.id)}
                          />
                        </div>
                      ))}
                    </div>
                  );
                })}
                {filteredGroups.length === 0 && (
                  <Text
                    color="gray60"
                    cx={css.emptyMessage}
                    fontSize="14"
                    size="24">
                    No results found
                  </Text>
                )}
              </div>
            </div>
          </DropdownContainer>
        )}
        renderTarget={(props) => (
          <div
            // eslint-disable-next-line max-len
            className={`${css.trigger} ${isDisabled ? css['trigger--disabled'] : ''} ${isOpen ? css['trigger--open'] : ''}`}
            {...props}>
            <Text
              color={value.length > 0 ? 'gray80' : 'gray60'}
              cx={css.triggerText}
              fontSize="14"
              size="none">
              {triggerLabel}
            </Text>
          </div>
        )}
        value={isOpen}
        onValueChange={handleOpenChange}
      />
      {selectedRoles.length > 0 && (
        <div className={css.tagList}>
          {selectedRoles.map((role) => (
            <Tag
              key={role.id}
              caption={role.label}
              isDisabled={isDisabled}
              size="24"
              onClear={() => toggleRole(role.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
